// Collector Stats DISPLAY helpers shared by web and mobile.
//
// Display only. The counts come from the backend's aggregate tables
// (media_coin_stats / collection_stats) as `collectorStats` on media, season and
// collection responses:
//   media:      { totalCollectors, bronze, silver, gold, legendary, updatedAt }
//   collection: { collectorCount, updatedAt }
// `collectorStats: null` means the counts could not be loaded — show nothing
// rather than a false "be the first".
//
// Copy rules: aggregate numbers only (never who), full numbers with thousands
// separators ("1,284 collectors" — no 1.2K), restrained zero states, at most one
// zero-tier note (Legendary), no urgency language.

import { AVAILABILITY_STATUS } from './collectionAvailability';

export const COLLECTOR_TIERS = Object.freeze([
    { key: 'bronze', label: 'Bronze' },
    { key: 'silver', label: 'Silver' },
    { key: 'gold', label: 'Gold' },
    { key: 'legendary', label: 'Legendary' },
]);

const toCount = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

/** 1284 → "1,284" (fixed en-US grouping so web and mobile render the same). */
export function formatCollectorNumber(value) {
    return toCount(value).toLocaleString('en-US');
}

/** 1 → "1 collector", 1284 → "1,284 collectors". */
export function formatCollectorCount(value) {
    const n = toCount(value);
    return `${formatCollectorNumber(n)} ${n === 1 ? 'collector' : 'collectors'}`;
}

/**
 * Media coin (movie / TV season) Collector Stats → render model, or null to
 * render nothing.
 *   { empty: true,  headline: 'Be the first to earn this coin.' }
 *   { empty: false, headline: '257 collectors', tiers: [{ key, label, count, display }],
 *     note: null | { title: 'No Legendary collectors yet.', body: 'Be the first to reach Legendary.' } }
 */
export function mediaCollectorStatsView(stats) {
    if (!stats || typeof stats !== 'object') return null;
    const total = toCount(stats.totalCollectors);
    if (total === 0) {
        return { empty: true, headline: 'Be the first to earn this coin.', tiers: [], note: null };
    }
    const tiers = COLLECTOR_TIERS.map(({ key, label }) => ({
        key, label, count: toCount(stats[key]), display: formatCollectorNumber(stats[key]),
    }));
    const legendary = tiers.find((t) => t.key === 'legendary');
    return {
        empty: false,
        headline: formatCollectorCount(total),
        tiers,
        note: legendary.count === 0
            ? { title: 'No Legendary collectors yet.', body: 'Be the first to reach Legendary.' }
            : null,
    };
}

const isEnded = (collection) => collection?.availabilityStatus === AVAILABILITY_STATUS.ENDED;
const isUpcoming = (collection) => collection?.availabilityStatus === AVAILABILITY_STATUS.UPCOMING;
const isSeasonalActive = (collection) => collection?.availabilityStatus === AVAILABILITY_STATUS.ACTIVE;
const rewardNameOf = (collection) => (collection?.reward?.name || '').trim() || null;

/**
 * Collection Detail line, or null to render nothing.
 *   43, active/permanent   → "43 collectors have earned" + reward "The Witching Hour"
 *   1                      → "1 collector has earned" + reward
 *   327, ended             → "327 collectors earned" + reward (the count stays forever)
 *   0, seasonal active     → "Be the first to unlock The Witching Hour."
 *   0, permanent           → "Be the first collector."
 *   0, ended               → "No collectors earned this limited-edition coin."
 *   0, upcoming            → null (nothing to earn yet)
 * Returns { empty, text, rewardName } — clients style `rewardName` separately.
 */
export function collectionCollectorView(collection) {
    const stats = collection?.collectorStats;
    if (!stats || typeof stats !== 'object') return null;
    const count = toCount(stats.collectorCount);
    const rewardName = rewardNameOf(collection);

    if (count > 0) {
        const verb = isEnded(collection) ? 'earned' : (count === 1 ? 'has earned' : 'have earned');
        return {
            empty: false,
            count,
            text: `${formatCollectorCount(count)} ${verb}`,
            rewardName,
        };
    }
    if (isUpcoming(collection)) return null;
    if (isEnded(collection)) {
        return { empty: true, count: 0, text: 'No collectors earned this limited-edition coin.', rewardName: null };
    }
    if (isSeasonalActive(collection)) {
        return {
            empty: true,
            count: 0,
            text: rewardName ? `Be the first to unlock ${rewardName}.` : 'Be the first to complete this collection.',
            rewardName: null,
        };
    }
    return { empty: true, count: 0, text: 'Be the first collector.', rewardName: null };
}

/**
 * Compact card label, or null to render nothing:
 * "43 collectors" · "1 collector" · "Be the first" (only while earnable).
 */
export function collectionCardCollectorLabel(collection) {
    const stats = collection?.collectorStats;
    if (!stats || typeof stats !== 'object') return null;
    const count = toCount(stats.collectorCount);
    if (count > 0) return formatCollectorCount(count);
    if (isEnded(collection) || isUpcoming(collection)) return null;
    return 'Be the first';
}
